<script lang="ts" setup>
import ThreadsNav from "@/components/mobile/ThreadsNav.vue";
import { computed, onBeforeMount, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStory } from "@/stores/story";
import { useMate } from "@/stores/mate";
import { Mate, Reply } from "@/types/entity";
import StoryCard from "@/components/mobile/story/story-chapter/StoryCard.vue";
import ReplyList from "@/components/mobile/reply/ReplyList.vue";
import ReplyInput, {
  ReplyInputInstance,
} from "@/components/mobile/reply/ReplyInput.vue";
import MomentActions from "@/components/mobile/popup/MomentActions.vue";
import HappyFaceIcon from "@/components/icon/HappyFaceIcon.vue";
import EmojiVotePopup from "@/components/mobile/popup/EmojiVotePopup.vue";
import ReactionCounter from "@/components/mobile/reply/ReactionCounter.vue";
import { getWordCount } from "@/modules/editor/editor-json-utils";
import { ReplyEntityType } from "@/enums";

const loading = ref(false);
const route = useRoute();
const router = useRouter();
const storyStore = useStory();
const mateStore = useMate();
const words = computed(() => {
  if (!currentStory.value) {
    return 0;
  }
  const json = storyStore.getJson(currentStory.value.text);
  return getWordCount(json);
});
const reactionCount = computed(() => {
  if (!currentStory.value) {
    return 0;
  }
  return currentStory.value.reply.interactivityEmoji.reduce(
    (acc, cur) => acc + cur.amount,
    0
  );
});
const author = ref<Mate | null>(null);
const currentStory = computed(() => {
  if (!route.params.uuid) {
    return null;
  }
  return storyStore.storyMap[route.params.uuid as string];
});
// 底部输入框状态
const replyTarget = ref<Reply | null>(null);
const replyInputRef = ref<ReplyInputInstance | null>(null);
const showActions = ref(false);
const toOperateReply = ref<Reply | null>(null);
const showEmoji = ref(false);

const prepareData = async () => {
  loading.value = true;
  try {
    const uuid: string = route.params.uuid as string;
    await storyStore.getStoryItem(uuid, true);
    // Story.text若为plainText，则转换为JSON并将Moment中的图片插入到JSON中
    author.value = await mateStore.getOrFetchMate(
      currentStory.value!.mate.uuid
    );
    replyTarget.value = currentStory.value!.reply;
    replyTarget.value.mate = author.value;
  } finally {
    loading.value = false;
  }
};
onBeforeMount(() => {
  prepareData();
});

const onReplyBtnClick = (replay: Reply) => {
  replyTarget.value = replay;
  replyInputRef.value?.focus();
};
const onReplyActionClick = (reply: Reply) => {
  toOperateReply.value = reply;
  showActions.value = true;
};
</script>
<template>
  <div class="h-full grid grid-rows-[auto_1fr_auto] relative">
    <threads-nav :enable-switch-mate="false" :show-back="true">
      <template #center>
        <div></div>
      </template>
    </threads-nav>
    <div v-if="loading" class="w-full h-full flex items-center justify-center">
      <span class="loading"></span>
    </div>
    <div v-else class="h-full overflow-y-auto">
      <story-card
        :story-book="currentStory"
        :cover-url="currentStory!.media?.url"
        :desc="currentStory?.description"
        :dt-publish="currentStory!.dtPublish"
        :mate="author!"
        :reaction-count="reactionCount"
        :reply-count="currentStory!.reply.replyCount"
        :title="currentStory!.title"
        :view-count="currentStory?.reply.viewerCount"
        :words="words"
      >
        <template #button-group>
          <reaction-counter :reply="currentStory?.reply" />
          <button
            class="btn btn-xs btn-neutral rounded"
            @click="
              showEmoji = true;
              toOperateReply = currentStory?.reply!;
            "
          >
            <happy-face-icon class="icon-sm" />
          </button>
        </template>
      </story-card>
      <div>
        <div class="font-bold text-base p-4">
          {{ $t("Texts.allComments") }} {{ currentStory!.reply.replyCount }}
        </div>
        <reply-list
          :root-id="currentStory!.reply.id"
          class="px-2"
          @on-reply-click="onReplyBtnClick"
          @on-reaction-click="onReplyActionClick"
        />
      </div>
    </div>
    <reply-input
      v-if="!loading && currentStory"
      ref="replyInputRef"
      :reply-target="replyTarget"
      :entity-type="ReplyEntityType.Story"
      :entity="currentStory"
    />
    <moment-actions v-model:show="showActions" :reply="toOperateReply" />
    <emoji-vote-popup
      v-model:show="showEmoji"
      :to-vote-reply="toOperateReply"
    />
  </div>
</template>
